import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment } from 'react';
import { 
  FaArrowLeft, FaCopy, FaChartLine, FaMousePointer, FaChartBar, 
  FaCheckCircle, FaExclamationCircle, FaClock, FaAd, FaFolder,
  FaMobile, FaDesktop, FaGlobe, FaTimes, FaBars, FaEdit, FaTrash,
  FaCheck, FaExternalLinkAlt, FaCalendarAlt, FaUsers, FaCode, FaPlay,
  FaApple, FaAndroid, FaFilter, FaImage, FaChevronDown, FaChevronUp
} from 'react-icons/fa';
import Chart from 'react-apexcharts';
import { 
  getToken, 
  getUser, 
  isTokenValid, 
  redirectToLogin,
  authenticatedFetch,
  getBaseUrl
} from '../utils/tokenUtils';
import StatsCard from '../components/StatsCard';
import UpgradeModal from '../components/UpgradeModal';
import InternalAdsHowToModal from '../components/InternalAdsHowToModal';
import EditPlacementModal from '../components/EditPlacementModal';

// Chart component is now ApexCharts
const formatNumberCompact = (num) => {
  if (num === null || num === undefined) return '0';
  const val = Number(num);
  if (isNaN(val)) return String(num);
  if (val >= 1000000) {
    return (val / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (val >= 1000) {
    return (val / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return val.toLocaleString();
};

const InternalAdsDetail = ({ user, onLogout, onStatsUpdate }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [placement, setPlacement] = useState(null);
  const [stats, setStats] = useState(null);
  const [projects, setProjects] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const fetchSeqRef = useRef(0);
  const initialLoadForPlacementRef = useRef(true);
  const [geoLoadingMore, setGeoLoadingMore] = useState(false);
  const [geographicRows, setGeographicRows] = useState([]);
  const [productGeoByProductId, setProductGeoByProductId] = useState({});
  const [productGeoLoading, setProductGeoLoading] = useState({});
  const [copied, setCopied] = useState(false);
  const [expandedActions, setExpandedActions] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalSection, setUpgradeModalSection] = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState('all');
  
  const COUNTRY_NAMES = {
    'TR': 'Turkey', 'US': 'United States', 'GB': 'United Kingdom', 'DE': 'Germany',
    'FR': 'France', 'IT': 'Italy', 'ES': 'Spain', 'BR': 'Brazil', 'RU': 'Russia',
    'CN': 'China', 'JP': 'Japan', 'KR': 'South Korea', 'IN': 'India', 'CA': 'Canada',
    'AU': 'Australia', 'NL': 'Netherlands', 'SE': 'Sweden', 'NO': 'Norway',
    'DK': 'Denmark', 'FI': 'Finland', 'AZ': 'Azerbaijan', 'UA': 'Ukraine',
    'PL': 'Poland', 'RO': 'Romania', 'KZ': 'Kazakhstan', 'UZ': 'Uzbekistan',
    'SA': 'Saudi Arabia', 'AE': 'United Arab Emirates', 'QA': 'Qatar', 'KW': 'Kuwait',
    'EG': 'Egypt', 'MA': 'Morocco', 'DZ': 'Algeria', 'ID': 'Indonesia', 'MY': 'Malaysia',
    'TH': 'Thailand', 'VN': 'Vietnam', 'PK': 'Pakistan', 'BD': 'Bangladesh',
    'MX': 'Mexico', 'AR': 'Argentina', 'CL': 'Chile', 'CO': 'Colombia', 'PE': 'Peru',
    'ZA': 'South Africa', 'NG': 'Nigeria', 'KE': 'Kenya', 'GH': 'Ghana', 'IE': 'Ireland',
    'CH': 'Switzerland', 'AT': 'Austria', 'BE': 'Belgium', 'PT': 'Portugal', 'GR': 'Greece'
  };

  const getCountryName = (code) => {
    if (!code || code === 'Unknown') return 'Unknown Location';
    return COUNTRY_NAMES[code.toUpperCase()] || code;
  };

  const getFlagUrl = (code) => {
    if (!code || code === 'Unknown') return null;
    return `https://flagcdn.com/w20/${code.toLowerCase()}.png`;
  };
  
  // Chart states
  const [chartMetric, setChartMetric] = useState('all'); // 'all', 'clicks', 'impressions', 'ctr'
  const [selectedProducts, setSelectedProducts] = useState([]); // For filtering
  const [hiddenProjects, setHiddenProjects] = useState(new Set()); // For project filtering
  const [dateRange, setDateRange] = useState('today'); // 'today', 'yesterday', '7', '30', '90'
  
  // Chart type based on metric
  const getChartType = () => {
    return chartMetric === 'ctr' ? 'line' : 'bar';
  };
  
  // Toast notification states
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  }, []);

  useEffect(() => {
    initialLoadForPlacementRef.current = true;
  }, [id]);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    if (initialLoadForPlacementRef.current) {
      setLoading(true);
    } else {
      setStatsLoading(true);
    }

    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }

      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const geoLimit = 5;
      const geoSkip = 0;
      const statsUrl = `/api/internal-ads/placements/${id}/stats?days=${encodeURIComponent(dateRange)}&country=${encodeURIComponent(selectedCountry)}&timezone=${encodeURIComponent(userTimezone)}&geoSkip=${geoSkip}&geoLimit=${geoLimit}`;

      const [placementResponse, statsResponse, placementsListResponse] = await Promise.all([
        authenticatedFetch(`/api/internal-ads/placements/${id}`),
        authenticatedFetch(statsUrl),
        authenticatedFetch('/api/internal-ads/placements?limit=100')
      ]);

      if (seq !== fetchSeqRef.current) return;

      if (placementResponse.ok) {
        const placementData = await placementResponse.json();
        setPlacement(placementData.placement);
      }

      if (placementsListResponse && placementsListResponse.ok) {
        const placementsListData = await placementsListResponse.json();
        setPlacements(placementsListData.placements || []);
      }

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
        setGeographicRows(statsData.countryStats || []);
      } else {
        const errText = await statsResponse.text().catch(() => '');
        console.error('Stats request failed:', statsResponse.status, errText);
        showToast('Error loading data.', 'error');
      }
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error fetching data:', error);
      showToast('Error loading data.', 'error');
    } finally {
      if (seq === fetchSeqRef.current) {
        initialLoadForPlacementRef.current = false;
        setLoading(false);
        setStatsLoading(false);
      }
    }
  }, [id, dateRange, selectedCountry, showToast]);

  useEffect(() => {
    setProductGeoByProductId({});
    setExpandedProduct(null);
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showEditModal) return;
    let cancelled = false;
    (async () => {
      try {
        if (!isTokenValid()) return;
        const projectsResponse = await authenticatedFetch('/api/dashboard/user/projects?limit=0');
        if (projectsResponse.ok && !cancelled) {
          const projectsData = await projectsResponse.json();
          setProjects(projectsData.projects || []);
        }
      } catch (e) {
        console.error('Error loading projects:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showEditModal]);

  // Listen header upgrade button event
  useEffect(() => {
    const onHeaderUpgrade = (e) => {
      const section = e?.detail?.section || 'internalads';
      setUpgradeModalSection(section);
      setShowUpgradeModal(true);
    };
    window.addEventListener('open-upgrade-modal', onHeaderUpgrade);
    return () => window.removeEventListener('open-upgrade-modal', onHeaderUpgrade);
  }, []);

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'android':
        return <FaAndroid className="w-4 h-4 text-green-500" />;
      case 'ios':
        return <FaApple className="w-4 h-4 text-gray-400" />;
      case 'web':
        return <FaGlobe className="w-4 h-4 text-blue-500" />;
      default:
        return <FaMobile className="w-4 h-4 text-gray-500" />;
    }
  };

  // Proje renkleri için utility fonksiyonu
  const getProjectColor = (projectName, index = null) => {
    const colors = [
      'rgba(99, 102, 241, 0.8)',   // Indigo
      'rgba(16, 185, 129, 0.8)',   // Emerald
      'rgba(249, 115, 22, 0.8)',   // Orange
      'rgba(236, 72, 153, 0.8)',   // Pink
      'rgba(6, 182, 212, 0.8)',    // Cyan
      'rgba(139, 92, 246, 0.8)',   // Violet
      'rgba(245, 158, 11, 0.8)',   // Amber
      'rgba(20, 184, 166, 0.8)',   // Teal
      'rgba(239, 68, 68, 0.8)',    // Crimson
      'rgba(132, 204, 22, 0.8)',   // Lime
      'rgba(217, 70, 239, 0.8)',   // Fuchsia
      'rgba(14, 165, 233, 0.8)',   // Sky
      'rgba(244, 63, 94, 0.8)',    // Rose
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(234, 179, 8, 0.8)'     // Yellow
    ];
    
    const borderColors = [
      'rgba(99, 102, 241, 1)',
      'rgba(16, 185, 129, 1)',
      'rgba(249, 115, 22, 1)',
      'rgba(236, 72, 153, 1)',
      'rgba(6, 182, 212, 1)',
      'rgba(139, 92, 246, 1)',
      'rgba(245, 158, 11, 1)',
      'rgba(20, 184, 166, 1)',
      'rgba(239, 68, 68, 1)',
      'rgba(132, 204, 22, 1)',
      'rgba(217, 70, 239, 1)',
      'rgba(14, 165, 233, 1)',
      'rgba(244, 63, 94, 1)',
      'rgba(59, 130, 246, 1)',
      'rgba(234, 179, 8, 1)'
    ];

    // Fallback for missing projectName
    if (!projectName && (index === null || index === undefined)) {
      return {
        backgroundColor: colors[0],
        borderColor: borderColors[0]
      };
    }

    let colorIndex;
    if (typeof index === 'number' && index >= 0) {
      colorIndex = index % colors.length;
    } else {
      // Proje adına göre hash oluştur
      let hash = 0;
      if (projectName) {
        for (let i = 0; i < projectName.length; i++) {
          hash = projectName.charCodeAt(i) + ((hash << 5) - hash);
        }
      }
      colorIndex = Math.abs(hash) % colors.length;
    }
    
    return {
      backgroundColor: colors[colorIndex],
      borderColor: borderColors[colorIndex]
    };
  };

  const loadMoreGeography = async () => {
    if (geoLoadingMore) return;
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      setGeoLoadingMore(true);
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const geoLimit = 5;
      const geoSkip = geographicRows.length;
      const url = `/api/internal-ads/placements/${id}/stats/geography?days=${encodeURIComponent(dateRange)}&country=${encodeURIComponent(selectedCountry)}&timezone=${encodeURIComponent(userTimezone)}&geoSkip=${geoSkip}&geoLimit=${geoLimit}`;
      const res = await authenticatedFetch(url);
      if (res.ok) {
        const data = await res.json();
        setGeographicRows((prev) => [...prev, ...(data.countryStats || [])]);
      }
    } catch (e) {
      console.error('Error loading geography:', e);
      showToast('Could not load more regions.', 'error');
    } finally {
      setGeoLoadingMore(false);
    }
  };

  const loadProductGeoPage = async (productId, append, explicitSkip) => {
    const key = String(productId);
    if (!append) {
      setProductGeoLoading((p) => ({ ...p, [key]: true }));
    }
    try {
      if (!isTokenValid()) {
        redirectToLogin();
        return;
      }
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const limit = 5;
      const prevLen = productGeoByProductId[key]?.items?.length ?? 0;
      const skip = append ? (explicitSkip !== undefined ? explicitSkip : prevLen) : 0;
      const url = `/api/internal-ads/placements/${id}/stats/product/${key}/geo?days=${encodeURIComponent(dateRange)}&country=${encodeURIComponent(selectedCountry)}&timezone=${encodeURIComponent(userTimezone)}&skip=${skip}&limit=${limit}`;
      const res = await authenticatedFetch(url);
      if (res.ok) {
        const data = await res.json();
        const newItems = data.items || [];
        setProductGeoByProductId((state) => {
          const existing = state[key]?.items || [];
          return {
            ...state,
            [key]: {
              items: append ? [...existing, ...newItems] : newItems,
              total: data.total ?? 0
            }
          };
        });
      }
    } catch (e) {
      console.error('Error loading product geo:', e);
      showToast('Could not load country breakdown.', 'error');
    } finally {
      setProductGeoLoading((p) => ({ ...p, [key]: false }));
    }
  };

  const getPlacementStatusMeta = () => {
    if (!placement?.isActive || placement?.status === 'inactive') {
      return { color: 'bg-gray-400', title: 'Inactive' };
    }
    if (placement?.status === 'paused') {
      return { color: 'bg-yellow-400', title: 'Paused' };
    }
    return { color: 'bg-green-400', title: 'Active' };
  };

  const copyApiKey = async (apiKey, label = 'API key') => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      showToast(`${label} copied to clipboard!`, 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
      showToast(`Failed to copy ${label.toLowerCase()}`, 'error');
    }
  };

  // Generate all dates for the selected period
  const generateAllDates = () => {
    const now = new Date();
    let days;
    
    if (dateRange === 'all') {
      // En eski veriyi bul
      const dailyClickStats = stats?.dailyStats || [];
      const dailyImpressionStats = stats?.dailyImpressionStats || [];
      
      let oldestDate = null;
      
      // Click verilerinden en eski tarihi bul
      dailyClickStats.forEach(stat => {
        const date = new Date(stat._id.year, stat._id.month - 1, stat._id.day);
        if (!oldestDate || date < oldestDate) {
          oldestDate = date;
        }
      });
      
      // Impression verilerinden en eski tarihi bul
      dailyImpressionStats.forEach(stat => {
        const date = new Date(stat._id.year, stat._id.month - 1, stat._id.day);
        if (!oldestDate || date < oldestDate) {
          oldestDate = date;
        }
      });
      
      if (oldestDate) {
        // En eski veriden bugüne kadar
        const diffTime = Math.abs(now - oldestDate);
        days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        // Çok fazla gün varsa maksimum 365 gün ile sınırla
        if (days > 365) {
          days = 365;
        }
      } else {
        // Veri yoksa son 7 gün
        days = 7;
      }
    } else if (dateRange === 'today' || dateRange === 'yesterday') {
      // Hourly view
      const hours = [];
      for (let i = 0; i < 24; i++) {
        hours.push(`${i.toString().padStart(2, '0')}:00`);
      }
      return hours;
    } else {
      days = parseInt(dateRange);
    }
    
    const dates = [];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      dates.push(`${day}/${month}/${year}`);
    }
    
    return dates;
  };

  // ApexCharts Configuration
  const getApexOptions = () => {
    const dates = generateAllDates();
    
    return {
      chart: {
        type: chartMetric === 'ctr' ? 'line' : 'area',
        height: 320,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'Inter, sans-serif',
        background: 'transparent',
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        }
      },
      colors: chartMetric === 'ctr' ? ['#F59E0B'] : chartMetric === 'impressions' ? ['#10B981'] : ['#3B82F6'],
      dataLabels: { enabled: false },
      stroke: {
        curve: 'smooth',
        width: 3,
        lineCap: 'round'
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.05,
          stops: [50, 100, 100]
        }
      },
      grid: {
        borderColor: '#1F2937',
        strokeDashArray: 4,
        padding: { top: 10, right: 20, bottom: 0, left: 10 }
      },
      xaxis: {
        categories: dates,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: { colors: '#9CA3AF', fontSize: '11px' }
        }
      },
      yaxis: {
        labels: {
          style: { colors: '#9CA3AF', fontSize: '11px' },
          formatter: (val) => chartMetric === 'ctr' ? `${val.toFixed(1)}%` : Math.floor(val)
        }
      },
      tooltip: {
        theme: 'dark',
        x: { show: true },
        y: {
          formatter: (val, { series, seriesIndex, dataPointIndex, w }) => {
            const seriesName = w.config.series[seriesIndex].name;
            if (seriesName.includes('%') || chartMetric === 'ctr') return `${val.toFixed(2)}%`;
            if (seriesName.includes('Impressions')) return `${val} impressions`;
            if (seriesName.includes('Clicks')) return `${val} clicks`;
            return `${val} ${chartMetric}`;
          }
        },
        style: { fontSize: '12px' },
        marker: { show: true }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'right',
        labels: { colors: '#FFFFFF' },
        markers: { radius: 12 }
      },
      markers: {
        size: 0,
        hover: { size: 5 }
      }
    };
  };

  const getApexSeries = () => {
    const dates = generateAllDates();
    const dailyStats = stats?.dailyStats || [];
    const dailyImpressionStats = stats?.dailyImpressionStats || [];
    const allProducts = stats?.productStats || [];
    const isHourly = dateRange === 'today' || dateRange === 'yesterday';
    const hourlyPeriod = stats?.hourlyPeriod;

    if (allProducts.length === 0) return [];

    const matchesSeriesPoint = (row, index, dateStr, productId) => {
      if (row._id.productId?.toString() !== productId) return false;
      if (!isHourly) {
        return `${row._id.day}/${row._id.month}/${row._id.year}` === dateStr;
      }
      if (
        hourlyPeriod &&
        row._id.year != null &&
        row._id.month != null &&
        row._id.day != null
      ) {
        return (
          row._id.hour === index &&
          row._id.year === hourlyPeriod.year &&
          row._id.month === hourlyPeriod.month &&
          row._id.day === hourlyPeriod.day
        );
      }
      return row._id.hour === index;
    };

    // Filter projects based on selectedProducts (if any are selected)
    const filteredProducts = selectedProducts.length > 0 
      ? allProducts.filter(p => selectedProducts.includes(p.productId))
      : allProducts;

    const series = [];

    filteredProducts.forEach((product, idx) => {
      const currentProductId = product.productId.toString();
      const projectName = product.projectName;
      const platform = product.productPlatform?.toLowerCase() || '';
      const platformIcon = platform.includes('ios') ? ' (iOS)' : platform.includes('android') ? ' (Android)' : ` (${platform})`;
      const productIndex = allProducts.findIndex(p => p.productId.toString() === currentProductId);
      const colors = getProjectColor(projectName, productIndex >= 0 ? productIndex : idx);

      // Clicks Series
      if (chartMetric === 'all' || chartMetric === 'clicks') {
        const clicksData = dates.map((date, index) => {
          const stat = dailyStats.find((s) => matchesSeriesPoint(s, index, date, currentProductId));
          return stat ? stat.clicks : 0;
        });
        
        series.push({
          name: `${projectName} ${platformIcon}${chartMetric === 'all' ? ' (Clicks)' : ''}`,
          data: clicksData,
          color: colors.borderColor
        });
      }

      // Impressions Series
      if (chartMetric === 'all' || chartMetric === 'impressions') {
        const impressionsData = dates.map((date, index) => {
          const stat = dailyImpressionStats.find((s) => matchesSeriesPoint(s, index, date, currentProductId));
          return stat ? stat.impressions : 0;
        });

        series.push({
          name: `${projectName} ${platformIcon}${chartMetric === 'all' ? ' (Impressions)' : ''}`,
          data: impressionsData,
          color: chartMetric === 'all' ? `${colors.borderColor}80` : colors.borderColor // More transparent for "all" impressions
        });
      }

      // CTR Series (Only if single metric is CTR)
      if (chartMetric === 'ctr') {
        const ctrData = dates.map((date, index) => {
          const cStat = dailyStats.find((s) => matchesSeriesPoint(s, index, date, currentProductId));
          const iStat = dailyImpressionStats.find((s) => matchesSeriesPoint(s, index, date, currentProductId));
          const impressions = iStat ? iStat.impressions : 0;
          const clicks = cStat ? cStat.clicks : 0;
          return impressions > 0 ? (clicks / impressions * 100) : 0;
        });

        series.push({
          name: `${projectName} ${platformIcon}`,
          data: ctrData,
          color: colors.borderColor
        });
      }
    });

    return series;
  };


  const handleDeletePlacement = async () => {
    try {
      const token = getToken();
      if (!token) {
        redirectToLogin();
        return;
      }

      const response = await fetch(`/api/internal-ads/placements/${placement._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showToast('Placement deleted successfully.');
        setTimeout(() => {
          navigate('/dashboard/internal-ads');
        }, 1000);
      } else {
        showToast('Failed to delete placement.', 'error');
      }
    } catch (error) {
      console.error('Error deleting placement:', error);
      showToast('An error occurred while deleting placement.', 'error');
    }
  };


  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <FaCheckCircle className="mr-1" />
          Active
        </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <FaExclamationCircle className="mr-1" />
          Inactive
        </span>;
      case 'paused':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <FaClock className="mr-1" />
          Paused
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Unknown
        </span>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle mobile actions menu
  const toggleActionsMenu = () => {
    setExpandedActions(prev => !prev);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!placement) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Placement not found</h2>
          <button
            onClick={() => navigate('/dashboard/internal-ads')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Back to Internal Ads
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 left-4 right-4 lg:left-auto lg:right-4 z-[60] px-6 py-3 rounded-lg shadow-lg transition-all duration-300 ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 
          toast.type === 'info' ? 'bg-blue-600 text-white' : 
          'bg-red-600 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      <div className="p-4 sm:p-6">
        {/* Placement Info */}
        <div>
              <div className="flex items-start lg:items-center justify-between mb-4">
                <div className="relative flex-1 min-w-0 pr-2" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center space-x-3 text-left hover:bg-gray-800/60 p-2 -m-2 rounded-xl transition-all duration-200 focus:outline-none group select-none max-w-full"
                  >
                    <div className="relative flex-shrink-0">
                      {placement.projectId?.logo ? (
                        <img 
                          src={placement.projectId.logo.startsWith('http') ? placement.projectId.logo : `${getBaseUrl()}${placement.projectId.logo}`}
                          alt="Project Logo" 
                          className="h-10 w-10 lg:h-12 lg:w-12 rounded-xl object-cover border border-gray-600"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                              e.target.nextSibling.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className="h-10 w-10 lg:h-12 lg:w-12 bg-gray-800 rounded-xl flex items-center justify-center"
                        style={{ display: placement.projectId?.logo ? 'none' : 'flex' }}
                      >
                        <FaAd className="w-5 h-5 lg:w-6 lg:h-6 text-gray-300" />
                      </div>
                      {/* Status badge - top-left of logo */}
                      {(() => {
                        const meta = getPlacementStatusMeta();
                        return (
                          <div
                            className={`absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-gray-900 ${meta.color}`}
                            title={meta.title}
                          />
                        );
                      })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row lg:items-center space-y-1 lg:space-y-0 lg:space-x-2">
                        <h2 className="text-lg lg:text-2xl font-bold text-white group-hover:text-indigo-400 transition-colors flex items-center">
                          <span className="truncate max-w-[200px] sm:max-w-xs md:max-w-md lg:max-w-lg">{placement.name}</span>
                          <FaChevronDown className={`w-3.5 h-3.5 ml-2 text-gray-400 group-hover:text-indigo-400 transition-all duration-200 flex-shrink-0 ${dropdownOpen ? 'transform rotate-180' : ''}`} />
                        </h2>
                        <span className="hidden lg:inline text-gray-400">•</span>
                        <span className="text-xs lg:text-base text-gray-400">
                          {placement.createdAt ? formatDate(placement.createdAt) : ''}
                        </span>
                      </div>
                      <p className="text-sm lg:text-base text-gray-400 truncate">
                        {placement.projectId?.name}
                      </p>
                    </div>
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute left-0 mt-3 w-80 rounded-xl bg-gray-900/95 border border-gray-800 shadow-2xl py-2 max-h-96 overflow-y-auto backdrop-blur-md z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Switch Placement
                      </div>
                      {placements.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 italic">
                          No other placements found
                        </div>
                      ) : (
                        placements.map((p) => {
                          const isActive = p._id === placement._id;
                          const pMeta = p.isActive && p.status !== 'inactive' 
                            ? (p.status === 'paused' ? { color: 'bg-yellow-400' } : { color: 'bg-green-400' })
                            : { color: 'bg-gray-400' };

                          return (
                            <button
                              key={p._id}
                              onClick={() => {
                                setDropdownOpen(false);
                                navigate(`/dashboard/internal-ads/${p._id}`);
                              }}
                              className={`w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-gray-800/60 text-left transition-colors ${isActive ? 'bg-indigo-600/10 border-l-2 border-indigo-500' : ''}`}
                            >
                              <div className="relative flex-shrink-0">
                                {p.projectId?.logo ? (
                                  <img 
                                    src={p.projectId.logo.startsWith('http') ? p.projectId.logo : `${getBaseUrl()}${p.projectId.logo}`}
                                    alt="Project Logo" 
                                    className="h-8 w-8 rounded-lg object-cover border border-gray-700"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextSibling) {
                                        e.target.nextSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className="h-8 w-8 bg-gray-800 rounded-lg flex items-center justify-center"
                                  style={{ display: p.projectId?.logo ? 'none' : 'flex' }}
                                >
                                  <FaAd className="w-4 h-4 text-gray-400" />
                                </div>
                                <div className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border border-gray-900 ${pMeta.color}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-white truncate">
                                  {p.name}
                                </div>
                                <div className="text-xs text-gray-400 truncate">
                                  {p.projectId?.name || 'No Project'}
                                </div>
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
                
                {/* Spacing */}
                <div className="mb-4"></div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3 flex-shrink-0">
                  <button
                    onClick={() => setShowHowItWorks(true)}
                    className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                  >
                    <FaExternalLinkAlt className="mr-2" />
                    How it Works
                  </button>

                  {/* Edit Button */}
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors border border-gray-700"
                  >
                    <FaEdit className="mr-2" />
                    Edit
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-red-600 transition-colors border border-gray-700"
                  >
                    <FaTrash className="mr-2" />
                    Delete
                  </button>
                </div>
                
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 mt-4">
              <StatsCard
                title="Total Clicks"
                value={stats?.generalStats?.totalClicks || 0}
                icon={FaChartBar}
                className="scale-95 origin-left"
              />
              
              <StatsCard
                title="Successful Clicks"
                value={stats?.generalStats?.successfulClicks || 0}
                icon={FaCheckCircle}
                className="scale-95 origin-left"
              />
              
              <StatsCard
                title="Total Impressions"
                value={stats?.generalStats?.periodTotalImpressions ?? 0}
                icon={FaMousePointer}
                className="scale-95 origin-left"
              />
              
              <StatsCard
                title="Click Through Rate"
                value={`${(stats?.generalStats?.periodCtr ?? 0).toFixed(1)}%`}
                icon={FaChartLine}
                className="scale-95 origin-left"
              />
            </div>


            {/* Product Performance Chart */}
            <div className="bg-black rounded-lg border border-gray-800 p-4 lg:p-6 mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
                  <h3 className="text-base lg:text-lg font-bold text-white mb-4 lg:mb-0 flex items-center gap-2">
                    Performance Analytics
                    {statsLoading && (
                      <span className="text-xs font-normal text-indigo-400 animate-pulse">Updating…</span>
                    )}
                  </h3>
                  
                  {/* Chart Controls */}
                  <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
                    <div className="flex flex-wrap items-center gap-4">

                      {/* Metric Selector */}
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-300">Metric:</label>
                        <select
                          value={chartMetric}
                          onChange={(e) => setChartMetric(e.target.value)}
                          className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="all">All Metrics</option>
                          <option value="clicks">Clicks</option>
                          <option value="impressions">Impressions</option>
                          <option value="ctr">CTR</option>
                        </select>
                      </div>

                      {/* Date Range Selector */}
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-300">Period:</label>
                        <select
                          value={dateRange}
                          onChange={(e) => setDateRange(e.target.value)}
                          className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="today">Today</option>
                          <option value="yesterday">Yesterday</option>
                          <option value="7">Last 7 Days</option>
                          <option value="30">Last 30 Days</option>
                          <option value="90">Last 90 Days</option>
                        </select>
                      </div>

                      {/* Country Filter */}
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-300">Country:</label>
                        <select
                          value={selectedCountry}
                          onChange={(e) => setSelectedCountry(e.target.value)}
                          className="px-3 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="all">Global (All)</option>
                          {(stats?.countriesForFilter || []).map((code) => (
                            <option key={code} value={code}>
                              {getCountryName(code)}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Project Filter */}
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-300">Filter Apps:</label>
                        <div className="flex -space-x-1 overflow-hidden p-1">
                          {stats?.productStats?.map((product) => (
                            <button
                              key={product.productId}
                              onClick={() => {
                                if (selectedProducts.includes(product.productId)) {
                                  setSelectedProducts(selectedProducts.filter(id => id !== product.productId));
                                } else {
                                  setSelectedProducts([...selectedProducts, product.productId]);
                                }
                              }}
                              className={`relative w-8 h-8 rounded-lg border-2 transition-all ${
                                selectedProducts.length === 0 || selectedProducts.includes(product.productId)
                                  ? 'border-indigo-500 scale-110 z-10'
                                  : 'border-transparent opacity-40 grayscale'
                              }`}
                              title={product.projectName}
                            >
                              {product.projectLogo ? (
                                <img 
                                  src={product.projectLogo.startsWith('http') ? product.projectLogo : `${getBaseUrl()}${product.projectLogo}`}
                                  className="w-full h-full rounded-md object-cover"
                                  alt={product.projectName}
                                />
                              ) : (
                                <div className="w-full h-full bg-gray-700 flex items-center justify-center rounded-md">
                                  <span className="text-[10px] font-bold text-white">{(product.projectName || '?').substring(0, 1)}</span>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>


                {/* Chart Container */}
                <div className="h-[320px] w-full relative">
                  <Chart 
                    options={getApexOptions()} 
                    series={getApexSeries()} 
                    type={chartMetric === 'ctr' ? 'line' : 'area'} 
                    height="100%" 
                  />
                  {(!stats?.dailyStats?.length && !stats?.dailyImpressionStats?.length) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                      <div className="text-center">
                        <FaChartBar className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-400 mb-2">No Data</h3>
                        <p className="text-sm text-gray-500">
                          No data found for the selected period and filters.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

            {/* Project Performance Table */}
            <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-6 mb-8 mt-12 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-indigo-500/10 rounded-lg">
                    <FaChartBar className="text-indigo-500 h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-white tracking-tight">Project Performance</h3>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700/50">
                    <thead>
                      <tr className="bg-gray-800/30">
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                          Project
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                          Platform
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                          Impressions
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                          Clicks
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                          CTR Performance
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/50">
                      {stats?.productStats?.map((productStat, idx) => {
                        const impressions = productStat.impressions || 0;
                        const clicks = productStat.clicks || 0;
                        const ctr = impressions > 0 ? (clicks / impressions * 100) : 0;
                        
                        // CTR color based on performance
                        const getCtrColor = (val) => {
                          if (val > 5) return 'text-green-400';
                          if (val > 1) return 'text-blue-400';
                          return 'text-yellow-500';
                        };

                        return (
                          <Fragment key={productStat.productId}>
                            <tr className="hover:bg-gray-800/30 transition-colors group">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center space-x-3">
                                <div 
                                  className="w-1.5 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                  style={{ backgroundColor: getProjectColor(productStat.projectName, idx).borderColor }}
                                ></div>
                                {productStat.projectLogo ? (
                                  <img 
                                    src={productStat.projectLogo.startsWith('http') 
                                      ? productStat.projectLogo 
                                      : `${getBaseUrl()}${productStat.projectLogo}`} 
                                    alt={productStat.projectName}
                                    className="w-8 h-8 rounded-lg object-cover border border-gray-700 shadow-sm"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'flex';
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className={`w-8 h-8 rounded-lg bg-gray-800 border border-gray-700 flex items-center justify-center ${productStat.projectLogo ? 'hidden' : 'flex'}`}
                                >
                                  <FaImage className="w-3.5 h-3.5 text-gray-500" />
                                </div>
                                <div className="flex flex-col">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-semibold text-white group-hover:text-indigo-400 transition-colors">
                                      {productStat.projectName}
                                    </span>
                                    <button 
                                      type="button"
                                      onClick={() => {
                                        const pid = productStat.productId?.toString?.() || productStat.productId;
                                        const opening = expandedProduct !== pid;
                                        setExpandedProduct(opening ? String(pid) : null);
                                        if (opening) {
                                          loadProductGeoPage(pid, false);
                                        }
                                      }}
                                      className="p-1 hover:bg-gray-700 rounded transition-colors text-gray-400"
                                    >
                                      {expandedProduct === (productStat.productId?.toString?.() || productStat.productId) ? <FaChevronUp className="w-3 h-3" /> : <FaChevronDown className="w-3 h-3" />}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-gray-800/50 border border-gray-700 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                                {getPlatformIcon(productStat.productPlatform)}
                                <span className="ml-2">{productStat.productPlatform}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-300" title={impressions.toLocaleString()}>
                              {formatNumberCompact(impressions)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-300" title={clicks.toLocaleString()}>
                              {formatNumberCompact(clicks)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col w-32">
                                <div className="flex justify-between items-center mb-1.5">
                                  <span className={`text-xs font-bold ${getCtrColor(ctr)}`}>
                                    {ctr.toFixed(1)}%
                                  </span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                                  <div 
                                    className={`h-full rounded-full transition-all duration-1000 ${
                                      ctr > 5 ? 'bg-green-500' : ctr > 1 ? 'bg-blue-500' : 'bg-yellow-500/50'
                                    }`}
                                    style={{ width: `${Math.min(ctr * 10, 100)}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                          </tr>
                          {expandedProduct === (productStat.productId?.toString?.() || String(productStat.productId)) && (
                            <tr className="bg-gray-800/10">
                              <td colSpan="5" className="px-6 py-4">
                                <div className="border-l-2 border-indigo-500/30 pl-4 py-2">
                                  <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center">
                                      <FaGlobe className="mr-2 h-3 w-3" /> Country Performance: {productStat.projectName} <span className="ml-2 flex items-center">{getPlatformIcon(productStat.productPlatform)}</span>
                                    </h4>
                                  </div>
                                  {productGeoLoading[productStat.productId?.toString?.() || String(productStat.productId)] ? (
                                    <div className="text-sm text-gray-500 py-4">Loading country data…</div>
                                  ) : (
                                  <div className="overflow-hidden rounded-xl border border-gray-800/50">
                                    <table className="min-w-full divide-y divide-gray-800/50">
                                      <thead className="bg-gray-900/50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Country</th>
                                          <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase">Imps</th>
                                          <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase">Clicks</th>
                                          <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase">CTR</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-800/50 bg-gray-900/20">
                                        {(productGeoByProductId[productStat.productId?.toString?.() || String(productStat.productId)]?.items || []).map((c) => {
                                          const flag = getFlagUrl(c.country);
                                          const cCtr = c.impressions > 0 ? (c.clicks / c.impressions * 100) : 0;
                                          return (
                                            <tr key={c.country} className="hover:bg-gray-800/20 transition-colors">
                                              <td className="px-4 py-2 whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                  {flag ? (
                                                    <img src={flag} alt={c.country} className="w-4 h-3 rounded-sm object-cover" />
                                                  ) : (
                                                    <div className="w-4 h-3 bg-gray-700 rounded-sm" />
                                                  )}
                                                  <span className="text-xs text-gray-300">{getCountryName(c.country)}</span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-right text-xs font-mono text-gray-400" title={c.impressions.toLocaleString()}>{formatNumberCompact(c.impressions)}</td>
                                              <td className="px-4 py-2 text-right text-xs font-mono text-gray-400" title={c.clicks.toLocaleString()}>{formatNumberCompact(c.clicks)}</td>
                                              <td className="px-4 py-2 text-right text-xs font-bold text-indigo-400">{cCtr.toFixed(1)}%</td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                    {(() => {
                                      const pid = productStat.productId?.toString?.() || String(productStat.productId);
                                      const geo = productGeoByProductId[pid];
                                      const hasMore = geo && geo.items && geo.total > geo.items.length;
                                      if (!hasMore) return null;
                                      return (
                                        <div className="p-3 border-t border-gray-800/50 bg-gray-900/30">
                                          <button
                                            type="button"
                                            onClick={() => loadProductGeoPage(pid, true, productGeoByProductId[pid]?.items?.length)}
                                            className="text-sm font-medium text-indigo-400 hover:text-indigo-300"
                                          >
                                            Load more countries
                                          </button>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

            {/* Geographic Distribution */}
            <div className="bg-gray-900/50 rounded-2xl border border-gray-800 p-6 mb-8 mt-12 backdrop-blur-sm">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-indigo-500/10 rounded-lg">
                    <FaGlobe className="text-indigo-500 h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-white tracking-tight">Geographic Distribution</h3>
                </div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
                  Live Market Data
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-800/50">
                  <thead>
                    <tr className="bg-gray-800/20">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                        Country
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                        Market Share (Impressions)
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                        Clicks
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-400 uppercase tracking-widest">
                        Conversion
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {geographicRows && geographicRows.length > 0 ? (
                      geographicRows.map((countryStat) => {
                        const totalImpressions = stats?.geoImpressionsGrandTotal || 0;
                        const share = totalImpressions > 0 ? (countryStat.impressions / totalImpressions * 100) : 0;
                        
                        return (
                          <tr key={countryStat.country} className="hover:bg-gray-800/30 transition-colors group">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                {getFlagUrl(countryStat.country) ? (
                                  <img 
                                    src={getFlagUrl(countryStat.country)} 
                                    alt={countryStat.country} 
                                    className="w-6 h-4 rounded-sm object-cover shadow-sm border border-gray-700" 
                                  />
                                ) : (
                                  <div className="w-6 h-4 rounded-sm bg-gray-800 border border-gray-700 flex items-center justify-center text-[8px] font-bold text-indigo-400">
                                    {!countryStat.country || countryStat.country === 'Unknown' ? '??' : countryStat.country.substring(0, 2).toUpperCase()}
                                  </div>
                                )}
                                <span className="text-sm font-medium text-gray-200 group-hover:text-white transition-colors">
                                  {getCountryName(countryStat.country)}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col w-48">
                                <div className="flex justify-between items-center mb-1.5">
                                  <span className="text-xs font-mono text-gray-400" title={countryStat.impressions.toLocaleString()}>{formatNumberCompact(countryStat.impressions)}</span>
                                  <span className="text-xs font-bold text-indigo-400">{share.toFixed(1)}%</span>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-1 overflow-hidden">
                                  <div 
                                    className="h-full bg-indigo-500 rounded-full transition-all duration-1000"
                                    style={{ width: `${share}%` }}
                                  ></div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-400" title={countryStat.clicks.toLocaleString()}>
                              {formatNumberCompact(countryStat.clicks)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`text-sm font-bold ${countryStat.ctr > 5 ? 'text-green-400' : 'text-gray-400'}`}>
                                {countryStat.ctr.toFixed(2)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="px-6 py-12 whitespace-nowrap text-sm text-gray-500 text-center">
                          <div className="flex flex-col items-center">
                            <FaGlobe className="w-8 h-8 text-gray-700 mb-3" />
                            <p>No geographic data available yet.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {geographicRows.length > 0 && geographicRows.length < (stats?.geoTotal || 0) && (
                <div className="flex justify-center mt-6">
                  <button
                    type="button"
                    onClick={loadMoreGeography}
                    disabled={geoLoadingMore}
                    className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm font-medium text-indigo-400 hover:bg-gray-700 disabled:opacity-50"
                  >
                    {geoLoadingMore ? 'Loading…' : 'Load more regions'}
                  </button>
                </div>
              )}
            </div>
      </div>

      {/* How it Works Modal */}
      <InternalAdsHowToModal
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
        placement={placement}
        onCopyApiKey={copyApiKey}
      />

    {/* Upgrade Modal */}
    <UpgradeModal
      isOpen={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
      user={user}
      onUpgrade={() => setShowUpgradeModal(false)}
      highlightSection={upgradeModalSection}
    />

      {/* Edit Modal */}
      {showEditModal && placement && (
        <EditPlacementModal
          placement={placement}
          projects={projects}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchData();
            showToast('Placement updated successfully.');
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setShowDeleteModal(false)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-gray-900 p-6 text-left align-middle shadow-xl transition-all border border-gray-700">
                  <DialogTitle as="h3" className="text-lg font-medium leading-6 text-white flex justify-between items-center mb-4">
                    Delete Placement
                    <button onClick={() => setShowDeleteModal(false)} className="text-gray-400 hover:text-white">
                      <FaTimes />
                    </button>
                  </DialogTitle>

                  <div className="mb-6">
                    <p className="text-gray-300">
                      Are you sure you want to delete this placement? This action cannot be undone and will remove all associated statistics.
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setShowDeleteModal(false);
                        handleDeletePlacement();
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default InternalAdsDetail;
